// Where a take lands, and what a take that has never left the browser is
// called — the pure part of recording in the room.
//
// A take is the mic from the moment the transport rolled to the moment it was
// stopped. Two things stand between that and a clip:
//
// - The mic hears late. What the producer played against beat 1 arrives
//   `offsetMs` into the recording, so the take is placed that much earlier;
//   a take started at 0 has nowhere earlier to go and loses its head instead.
// - A looping transport wraps and the recorder doesn't. Everything after the
//   first wrap was played against a timeline that had jumped back, so it is
//   cut: the first pass is the take.
//
// A LOCAL room has no jam and so no pool to put a take in until it is saved.
// Its takes are held in the browser under a key no pool key can collide with
// (`local/…`), and the save uploads them and swaps the real keys in before
// the room goes up (`rekeyAudio`).

import type { CanonicalRoom, CanonicalTrackState } from "@shared/sync/state.js";

export interface TakeWindow {
  /** Seconds into the take to keep, half-open. */
  fromSec: number;
  toSec: number;
  /** Timeline second the kept part begins at. */
  atSec: number;
}

/**
 * The part of a take that becomes the clip. The take began recording at
 * timeline `startSec` and is `durationSec` long; `offsetSec` is the input
 * latency; `loopEndSec` is where a looping transport wrapped (null when it
 * plays through). Null when nothing is left to keep.
 */
export function takeWindow(
  startSec: number,
  durationSec: number,
  offsetSec: number,
  loopEndSec: number | null = null,
): TakeWindow | null {
  const at = startSec - Math.max(0, offsetSec);
  const fromSec = Math.max(0, -at);
  let toSec = durationSec;
  if (loopEndSec !== null && loopEndSec > startSec) toSec = Math.min(toSec, loopEndSec - at);
  if (!(toSec - fromSec > 1e-3)) return null;
  return { fromSec, toSec, atSec: Math.max(0, at) };
}

/**
 * The lane a take lands on: the first ARMED audio lane in display order —
 * arm is the Set's own record flag, so the header's ● is the picker — else
 * the lane the producer has selected, when it is audio. A phone has no ● in
 * its lane headers; tapping the lane is how it says where.
 */
export function recordTarget(room: CanonicalRoom, fallbackLaneId: string | null): CanonicalTrackState | null {
  const audio = [...room.tracks]
    .filter((t) => (t.trackKind ?? "audio") === "audio")
    .sort((a, b) => a.ord - b.ord);
  return audio.find((t) => t.arm) ?? audio.find((t) => t.id === fallbackLaneId) ?? null;
}

const LOCAL_PREFIX = "local/take-";

/** A fresh key for a take held in this browser. */
export function mintLocalTakeKey(): string {
  return `${LOCAL_PREFIX}${crypto.randomUUID()}.wav`;
}

export function isLocalTakeKey(key: string | null | undefined): key is string {
  return typeof key === "string" && key.startsWith(LOCAL_PREFIX);
}

/** The local takes a room's clips still point at, each once. */
export function localTakeKeysOf(room: CanonicalRoom): string[] {
  const out = new Set<string>();
  for (const c of room.clips) if (isLocalTakeKey(c.audioKey)) out.add(c.audioKey);
  return [...out];
}

/**
 * The room with every local take swapped for the pool key it was uploaded
 * under. Throws when a clip points at a local take the map doesn't cover — a
 * room that went up with one would name audio nobody can fetch.
 */
export function rekeyAudio(room: CanonicalRoom, keys: ReadonlyMap<string, string>): CanonicalRoom {
  return {
    ...room,
    clips: room.clips.map((c) => {
      if (!isLocalTakeKey(c.audioKey)) return c;
      const key = keys.get(c.audioKey);
      if (key === undefined) throw new Error(`A take on "${c.name}" never uploaded — nothing was saved.`);
      return { ...c, audioKey: key };
    }),
  };
}
